import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Space, Spin } from 'antd';
import { FaArrowLeft } from 'react-icons/fa';
import { IoCloudDownloadOutline } from 'react-icons/io5';
import { PageHeader } from '@/components/common/PageHeader';
import { CustomerDetailsHeader, CreditHistoryTable } from '../components';
import { useCustomerDetail, useCreditControlHistory } from '../hooks';
import { exportToCSV, generateCSVFilename, formatDateForCSV } from '@/utils/csv';
import { formatDealerAmount } from '@/utils/currency';

export const CustomerDetailsPage: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();

  const { data: customer, isLoading: customerLoading } = useCustomerDetail(accountId || '');
  const { data: creditHistory, isLoading: historyLoading } = useCreditControlHistory(accountId || '');

  const handleBack = () => {
    navigate('/customers');
  };

  const handleExportCreditHistory = () => {
    const rows = creditHistory?.data || [];
    const dealersCurrency = customer?.dogma_DealersCurrency;
    const exchangeRate = dealersCurrency?.dogma_exchangerate ?? 1;
    const csvData = rows.map((r: any) => ({
      'Name': r.dogma_name || '-',
      'Type': r['dogma_type@OData.Community.Display.V1.FormattedValue'] || '-',
      'Credit Limit': r.dogma_creditlimit != null ? formatDealerAmount(r.dogma_creditlimit, dealersCurrency, exchangeRate) : '-',
      'Available Credit': r.dogma_availablecredit != null ? formatDealerAmount(r.dogma_availablecredit, dealersCurrency, exchangeRate) : '-',
      'Created Date': r.createdon ? formatDateForCSV(r.createdon) : '-',
    }));
    const columns = [
      { key: 'Name' as const, header: 'Name' },
      { key: 'Type' as const, header: 'Type' },
      { key: 'Credit Limit' as const, header: 'Credit Limit' },
      { key: 'Available Credit' as const, header: 'Available Credit' },
      { key: 'Created Date' as const, header: 'Created Date' },
    ];
    exportToCSV(csvData, columns, generateCSVFilename('credit-history'));
  };

  if (!accountId) {
    return (
      <div className="p-4">
        <div className="text-center py-8 text-gray-500">
          Invalid customer ID
        </div>
      </div>
    );
  }

  if (customerLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" tip="Loading customer details..." />
      </div>
    );
  }

  return (
    <div className="p-4">
      <PageHeader
        title={customer?.name || 'Customer Details'}
        subtitle="View customer information and credit history"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Customers', path: '/customers' },
          { label: customer?.name || 'Details' },
        ]}
        actions={
          <Button
            icon={<IoCloudDownloadOutline />}
            onClick={handleExportCreditHistory}
            disabled={!creditHistory?.data?.length}
          >
            Export Credit History
          </Button>
        }
      />

      <Space direction="vertical" size="large" className="w-full">
        {/* Customer Details Header */}
        <CustomerDetailsHeader customer={customer} loading={customerLoading} />

        {/* Credit History Table */}
        <CreditHistoryTable
          data={creditHistory?.data || []}
          loading={historyLoading}
          dealersCurrency={customer?.dogma_DealersCurrency}
          exchangeRate={customer?.dogma_DealersCurrency?.dogma_exchangerate}
        />
      </Space>
    </div>
  );
};

export default CustomerDetailsPage;
